import { useState, useRef, useEffect } from "react";
import { 
  Mic, MicOff, Volume2, VolumeX, X, Brain, Sparkles, 
  Loader2, CheckCircle, AlertCircle, Phone, FileText,
  Calendar, CalendarPlus, Check, XCircle
} from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * 🎤 Popup d'interaction vocale avec Christelle
 * - Enregistrement vocal
 * - Transcription avec Whisper
 * - Recherche intelligente OU Création de RDV
 * - Réponse vocale avec TTS
 */
export default function ChristelleVoicePopup({ isOpen, onClose, onResultClick, onRdvCreated }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [status, setStatus] = useState("idle"); // idle, recording, processing, speaking, done, error, confirm_rdv
  const [transcribedText, setTranscribedText] = useState("");
  const [responseText, setResponseText] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  
  // État pour la création de RDV
  const [pendingRdv, setPendingRdv] = useState(null);
  const [rdvCreated, setRdvCreated] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  
  // Nettoyer à la fermeture
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);
  
  // Reset quand le popup s'ouvre
  useEffect(() => {
    if (isOpen) {
      setStatus("idle");
      setTranscribedText("");
      setResponseText("");
      setResults([]);
      setError("");
      setPendingRdv(null);
      setRdvCreated(false);
    }
  }, [isOpen]);
  
  // Démarrer l'enregistrement
  const startRecording = async () => {
    try {
      setError("");
      setStatus("recording");
      setIsRecording(true);
      audioChunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000
        } 
      });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        await processAudio();
      };
      
      mediaRecorder.start(100);
      
    } catch (err) {
      console.error("Erreur accès micro:", err);
      setError("Impossible d'accéder au microphone. Vérifiez les permissions.");
      setStatus("error");
      setIsRecording(false);
    }
  };
  
  // Arrêter l'enregistrement
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  
  // Détecter si c'est une demande de RDV
  const isRdvRequest = (text) => {
    const rdvKeywords = [
      "rdv", "rendez-vous", "rendez vous", "planifier", "planifie",
      "ajouter", "ajoute", "créer", "crée", "prendre", "prends",
      "réserver", "réserve", "bloquer", "bloque", "visite", "intervention",
      "positionner", "positionne", "fixer", "fixe", "caler", "programmer"
    ];
    const textLower = text.toLowerCase();
    return rdvKeywords.some(kw => textLower.includes(kw));
  };
  
  // Traiter l'audio enregistré
  const processAudio = async () => {
    setStatus("processing");
    setIsProcessing(true);
    
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      const reader = new FileReader();
      const audioBase64 = await new Promise((resolve) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(audioBlob);
      });
      
      // Appeler directement l'endpoint RDV qui fait tout en un seul appel
      // Il transcrit ET analyse l'intention
      const rdvResponse = await authFetch(`${API}/christelle/action/voice-rdv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio_base64: audioBase64 })
      });
      
      if (!rdvResponse.ok) {
        throw new Error("Erreur lors du traitement vocal");
      }
      
      const rdvData = await rdvResponse.json();
      const query = rdvData.query || "";
      setTranscribedText(query);
      
      // Si c'est bien une demande de RDV
      if (rdvData.parsed?.is_rdv_request) {
        setResponseText(rdvData.response_text || "");
        
        // Si prêt à créer, stocker les infos
        if (rdvData.ready_to_create && rdvData.parsed) {
          setPendingRdv(rdvData.parsed);
          setStatus("confirm_rdv");
        } else {
          setStatus("done");
        }
        
        // Jouer la réponse audio
        if (rdvData.response_audio_base64) {
          playAudio(rdvData.response_audio_base64);
        }
        
        setIsProcessing(false);
        return;
      }
      
      // Sinon, c'est une recherche - appeler l'endpoint de recherche
      const searchResponse = await authFetch(`${API}/christelle/voice/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio_base64: audioBase64 })
      });
      
      if (searchResponse.ok) {
        const searchData = await searchResponse.json();
        setResponseText(searchData.response_text || "");
        setResults(searchData.results || []);
        
        if (searchData.response_audio_base64) {
          playAudio(searchData.response_audio_base64);
        } else {
          setStatus("done");
        }
      } else {
        throw new Error("Erreur lors de la recherche");
      }
      
    } catch (err) {
      console.error("Erreur traitement audio:", err);
      setError(err.message || "Erreur lors du traitement vocal");
      setStatus("error");
    }
    
    setIsProcessing(false);
  };
  
  // Jouer un audio base64
  const playAudio = (base64Audio) => {
    setStatus("speaking");
    setIsSpeaking(true);
    
    const audioSrc = `data:audio/mpeg;base64,${base64Audio}`;
    const audio = new Audio(audioSrc);
    audioRef.current = audio;
    
    audio.onended = () => {
      setIsSpeaking(false);
      if (pendingRdv) {
        setStatus("confirm_rdv");
      } else {
        setStatus("done");
      }
    };
    
    audio.onerror = () => {
      setIsSpeaking(false);
      setStatus("done");
    };
    
    audio.play();
  };
  
  // Confirmer la création du RDV
  const confirmRdv = async () => {
    if (!pendingRdv) return;
    
    setIsProcessing(true);
    setStatus("processing");
    
    try {
      const response = await authFetch(`${API}/christelle/action/create-rdv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: pendingRdv.client_name,
          date: pendingRdv.date,
          heure: pendingRdv.heure,
          type_rdv: pendingRdv.type_rdv || "r1",
          technicien: pendingRdv.technicien || null,
          notes: pendingRdv.notes,
          duree_minutes: pendingRdv.duree_minutes || 60
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          setRdvCreated(true);
          const techInfo = pendingRdv.technicien ? ` avec ${pendingRdv.technicien}` : "";
          setResponseText(`✅ RDV créé avec succès pour ${data.message || pendingRdv.client_name}${techInfo}`);
          setStatus("done");
          
          // Générer une réponse vocale de confirmation
          const ttsResponse = await authFetch(`${API}/christelle/voice/speak?text=${encodeURIComponent("Le rendez-vous a été créé avec succès.")}`, {
          method: 'POST'
        });
        
        if (ttsResponse.ok) {
          const audioBlob = await ttsResponse.blob();
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          audio.play();
        }
        
        if (onRdvCreated) {
          onRdvCreated(data);
        }
        } else {
          // RDV non créé - afficher l'erreur
          setError(data.message || "Client non trouvé");
          setResponseText(data.message || "Je n'ai pas pu créer le RDV. Le client n'a pas été trouvé.");
          setStatus("error");
        }
      } else {
        throw new Error("Erreur lors de la création du RDV");
      }
      
    } catch (err) {
      console.error("Erreur création RDV:", err);
      setError("Impossible de créer le RDV");
      setStatus("error");
    }
    
    setIsProcessing(false);
  };
  
  // Annuler la création du RDV
  const cancelRdv = () => {
    setPendingRdv(null);
    setStatus("done");
    setResponseText("RDV annulé. Vous pouvez faire une nouvelle demande.");
  };
  
  // Arrêter la lecture audio
  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
    if (pendingRdv) {
      setStatus("confirm_rdv");
    } else {
      setStatus("done");
    }
  };
  
  // Rejouer la réponse
  const replaySpeech = () => {
    if (audioRef.current) {
      setStatus("speaking");
      setIsSpeaking(true);
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-purple-500/30 shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 p-4 border-b border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  Christelle IA
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </h2>
                <p className="text-xs text-purple-300">Assistant vocal</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Contenu principal */}
        <div className="p-6">
          {/* Indicateur de statut */}
          <div className="text-center mb-6">
            {status === "idle" && (
              <p className="text-slate-400">Appuyez sur le micro pour parler à Christelle</p>
            )}
            {status === "recording" && (
              <p className="text-red-400 animate-pulse">🎤 Parlez maintenant...</p>
            )}
            {status === "processing" && (
              <p className="text-amber-400 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Christelle réfléchit...
              </p>
            )}
            {status === "speaking" && (
              <p className="text-green-400 flex items-center justify-center gap-2">
                <Volume2 className="w-4 h-4 animate-pulse" />
                Christelle répond...
              </p>
            )}
            {status === "confirm_rdv" && (
              <p className="text-amber-400 flex items-center justify-center gap-2">
                <CalendarPlus className="w-4 h-4" />
                Confirmer le RDV ?
              </p>
            )}
            {status === "done" && !rdvCreated && (
              <p className="text-purple-400 flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Terminé
              </p>
            )}
            {status === "done" && rdvCreated && (
              <p className="text-green-400 flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                RDV créé !
              </p>
            )}
            {status === "error" && (
              <p className="text-red-400 flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error || "Une erreur s'est produite"}
              </p>
            )}
          </div>
          
          {/* Bouton micro principal */}
          <div className="flex justify-center mb-6">
            {!isRecording ? (
              <button
                onClick={startRecording}
                disabled={isProcessing || isSpeaking || status === "confirm_rdv"}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all transform hover:scale-105 ${
                  isProcessing || isSpeaking || status === "confirm_rdv"
                    ? "bg-slate-700 cursor-not-allowed"
                    : "bg-gradient-to-br from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 shadow-lg shadow-purple-500/30"
                }`}
              >
                <Mic className="w-8 h-8 text-white" />
              </button>
            ) : (
              <button
                onClick={stopRecording}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 flex items-center justify-center transition-all transform hover:scale-105 shadow-lg shadow-red-500/30 animate-pulse"
              >
                <MicOff className="w-8 h-8 text-white" />
              </button>
            )}
          </div>
          
          {/* Texte transcrit */}
          {transcribedText && (
            <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xs text-slate-500 mb-1">Votre demande :</p>
              <p className="text-white">{transcribedText}</p>
            </div>
          )}
          
          {/* Réponse de Christelle */}
          {responseText && (
            <div className={`mb-4 p-3 rounded-lg border ${
              rdvCreated 
                ? "bg-green-900/30 border-green-500/30" 
                : "bg-purple-900/30 border-purple-500/30"
            }`}>
              <div className="flex items-center justify-between mb-1">
                <p className={`text-xs ${rdvCreated ? "text-green-400" : "text-purple-400"}`}>
                  Réponse de Christelle :
                </p>
                {audioRef.current && !rdvCreated && (
                  <button
                    onClick={isSpeaking ? stopSpeaking : replaySpeech}
                    className="p-1 rounded hover:bg-purple-500/20 transition-colors"
                  >
                    {isSpeaking ? (
                      <VolumeX className="w-4 h-4 text-purple-400" />
                    ) : (
                      <Volume2 className="w-4 h-4 text-purple-400" />
                    )}
                  </button>
                )}
              </div>
              <p className={`text-sm ${rdvCreated ? "text-green-100" : "text-purple-100"}`}>
                {responseText}
              </p>
            </div>
          )}
          
          {/* Boutons de confirmation RDV */}
          {status === "confirm_rdv" && pendingRdv && (
            <div className="mb-4 p-4 bg-amber-900/20 rounded-lg border border-amber-500/30">
              <p className="text-sm text-amber-200 mb-3">
                📅 <strong>{pendingRdv.type_rdv?.toUpperCase() || "RDV"}</strong> pour <strong>{pendingRdv.client_name}</strong>
                <br />
                Le <strong>{pendingRdv.date}</strong> à <strong>{pendingRdv.heure}</strong>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmRdv}
                  disabled={isProcessing}
                  className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-500 disabled:bg-green-800 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Confirmer
                </button>
                <button
                  onClick={cancelRdv}
                  disabled={isProcessing}
                  className="flex-1 py-2 px-4 bg-slate-600 hover:bg-slate-500 disabled:bg-slate-800 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  Annuler
                </button>
              </div>
            </div>
          )}
          
          {/* Résultats de recherche */}
          {results.length > 0 && !pendingRdv && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Résultats trouvés :</p>
              {results.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => result.row_number && onResultClick?.(result.row_number)}
                  className={`w-full p-3 bg-slate-800/50 rounded-lg border border-slate-700 text-left transition-all ${
                    result.row_number ? "hover:border-purple-500/50 hover:bg-slate-800 cursor-pointer" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      result.source === "call" ? "bg-green-500/20 text-green-400" : "bg-purple-500/20 text-purple-400"
                    }`}>
                      {result.source === "call" ? (
                        <Phone className="w-4 h-4" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{result.client_name}</p>
                      <p className="text-xs text-slate-500">
                        {result.source === "call" ? "Appel" : "Fiche"} • {result.relevance_score}% pertinent
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-4">
          <p className="text-xs text-center text-slate-500">
            💡 Recherche : "Clients mécontents" | 📅 RDV : "Planifie un RDV pour Dupont demain 14h"
          </p>
        </div>
      </div>
    </div>
  );
}
